import { dateSuisse } from './dates';

/**
 * Les paiements d'une facture écrits dans ses messages (« Un acompte de
 * CHF 7'807.– a été versé le 24.12.2025 ») et ceux inscrits à leur date.
 *
 * <p>Sans dépendance au calcul des totaux : le décompte de TVA (`vat.js`) en
 * a besoin pour situer un acompte reçu l'année avant sa facture, comme la
 * position TVA du serveur, et `acomptes-anterieurs.js` dépend lui-même de
 * `vat.js`.
 */

/**
 * Les paiements d'une facture : ceux inscrits à leur date (« Paiements
 * reçus »), et ceux écrits dans son message du bas qui n'y sont pas encore.
 *
 * <p>L'acompte écrit en bas de la facture (« acompte de CHF 7'807.– versé le
 * 24.12.2025 ») ne s'inscrivait qu'à l'ouverture de la facture : une facture
 * reprise et jamais rouverte comptait tout en 2026.
 *
 * @param {object} facture facture
 * @param {{date: string, montant: number}[]} [inscrits] paiements inscrits, date suisse
 * @returns {{date: string, montant: number}[]} les paiements, date suisse
 */
export function paiementsDe(facture, inscrits) {
  const liste = inscrits || [];
  const ecrits = paiementsDansLesRemarques(
    [facture?.intro, facture?.piedMessage, facture?.notes].filter(Boolean).join('\n'),
  )
    .map((p) => ({ date: dateSuisse(p.date), montant: p.montant }))
    .filter((p) => !liste.some((i) => i.date === p.date && Math.abs(i.montant - p.montant) < 0.01));
  return [...liste, ...ecrits];
}

const MONTANT = "(\\d{1,3}(?:['’ \\u00a0]\\d{3})*(?:[.,]\\d{1,2})?)";

/**
 * Les paiements écrits dans les remarques d'une facture : « Un acompte d'un
 * montant de CHF 7'807.– a été versé le 24.12.2025 », « Un acompte de 3 070
 * CHF a été versé le 18.12.2025 », « Un complément de CHF 4'500.– a été versé
 * le 12.02.2026 ».
 *
 * @param {string} texte remarques de la facture
 * @returns {{date: string, montant: number}[]} les paiements trouvés, date ISO
 */
export function paiementsDansLesRemarques(texte) {
  const sortie = [];
  const motif = new RegExp(
    '(acompte|compl[ée]ment|versement|paiement|solde)[^.]*?' +
      '(?:CHF\\s*' +
      MONTANT +
      '|' +
      MONTANT +
      '\\s*(?:CHF|fr\\.?|francs))' +
      '[^0-9]{0,60}?(?:vers[ée]|pay[ée]|re[çc]u|effectu[ée])[^0-9]{0,15}?le\\s+(\\d{1,2})[./](\\d{1,2})[./](\\d{4})',
    'gi',
  );
  for (const m of String(texte || '').matchAll(motif)) {
    const brut = (m[2] || m[3] || '').replace(/['’ \u00a0]/g, '').replace(',', '.');
    const montant = Number(brut);
    if (!(montant > 0)) continue;
    const date = m[6] + '-' + m[5].padStart(2, '0') + '-' + m[4].padStart(2, '0');
    sortie.push({ date, montant });
  }
  // La date avant le montant : « Acompte versé le 24.12.2025 : CHF 7'807.– ».
  const inverse = new RegExp(
    '(acompte|compl[ée]ment|versement|paiement|solde)[^.0-9]*?' +
      '(?:vers[ée]|pay[ée]|re[çc]u|effectu[ée])[^0-9]{0,15}?le\\s+(\\d{1,2})[./](\\d{1,2})[./](\\d{4})' +
      '[^0-9]{0,40}?(?:CHF\\s*' +
      MONTANT +
      '|' +
      MONTANT +
      '\\s*(?:CHF|fr\\.?|francs))',
    'gi',
  );
  for (const m of String(texte || '').matchAll(inverse)) {
    const brut = (m[5] || m[6] || '').replace(/['’ \u00a0]/g, '').replace(',', '.');
    const montant = Number(brut);
    if (!(montant > 0)) continue;
    const date = m[4] + '-' + m[3].padStart(2, '0') + '-' + m[2].padStart(2, '0');
    if (!sortie.some((p) => p.date === date && p.montant === montant))
      sortie.push({ date, montant });
  }
  return sortie;
}

/**
 * Ce qu'une facture a fait entrer sur le compte pendant une période : ses
 * paiements inscrits et écrits, chacun à sa date ; sans paiement inscrit, le
 * reste perçu au jour du paiement de la facture.
 *
 * <p>L'acompte versé en décembre 2025, écrit en bas de la facture de 2026
 * payée en 2026, se recomptait dans l'argent reçu de 2026 : il est déclaré
 * en 2025.
 *
 * @param {object} facture facture
 * @param {{date: string, montant: number}[]} [inscrits] paiements inscrits, date suisse
 * @param {number} percu ce que la facture a reçu en tout (sans paiement inscrit)
 * @param {(date: string) => boolean} dansLaPeriode la période voulue
 * @returns {number} l'argent reçu pendant la période
 */
export function recuDansLaPeriode(facture, inscrits, percu, dansLaPeriode) {
  const connus = paiementsDe(facture, inscrits);
  if (inscrits?.length) {
    return connus.filter((p) => dansLaPeriode(p.date)).reduce((s, p) => s + (+p.montant || 0), 0);
  }
  if (!(percu > 0)) return 0;
  let ecrits = 0;
  let dedans = 0;
  connus.forEach((p) => {
    const m = Math.min(+p.montant || 0, percu - ecrits);
    if (m <= 0) return;
    ecrits += m;
    if (dansLaPeriode(p.date)) dedans += m;
  });
  const reste = percu - ecrits;
  return dedans + (reste > 0.005 && dansLaPeriode(facture.paidDate || facture.date) ? reste : 0);
}
